import { useState } from "react";
import { ScrollView, View, useWindowDimensions } from "react-native";
import { Activity, CalendarDays, Check, PencilLine } from "lucide-react-native";
import {
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  PrimaryButton,
  SecondaryButton,
  CompactActionButton,
  MoonNumericField,
  DateField,
  MoonFormField,
  FieldMessage,
  Row,
  semanticColors,
  TextField,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { useCreateTestResult } from "@src/packages/test-results/hooks/useTestResults";
import { TestResultDocumentAction } from "./TestResultDocumentAction";
import type { SelectedFile } from "@src/packages/document-vault/DocumentFileDropZone";
import {
  useAfcResults,
  type AfcResult,
} from "@src/packages/homepage/utils/ovarianReserveInputs";
import { getTodayString, parseDateOrTimestamp } from "@src/packages/utils/date";

const AFC_ILLUSTRATION =
  require("@src/apps/moon/assets/images/you-icons/test-tubes.png") as number;

export type AfcSelection = {
  value: number;
  sourceId?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (selection: AfcSelection) => void;
  onClear?: () => void;
  onOpenDocuments: (files: SelectedFile[]) => void;
  currentSourceId?: string | null;
  hasActiveAfc?: boolean;
};

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = parseDateOrTimestamp(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function ExistingResultRow({
  result,
  selected,
  onPress,
}: {
  result: AfcResult;
  selected: boolean;
  onPress: () => void;
}) {
  // The canonical Row on the input fill: a glass row's shadow is clipped by
  // the dialog's scroll area. The count stays a number so it never travels
  // as the analytics label.
  return (
    <Row
      surface="input"
      title={result.value}
      subtitle={result.date ? formatDate(result.date) : undefined}
      chevron={false}
      trailing={
        selected ? (
          <Check
            size={18}
            color={semanticColors.text.primary}
            strokeWidth={2.2}
          />
        ) : undefined
      }
      accessibilityState={{ selected }}
      action="afc_source_option"
      onPress={onPress}
    />
  );
}

export function AfcSourceSheet({
  open,
  onClose,
  onSelect,
  onClear,
  onOpenDocuments,
  currentSourceId,
  hasActiveAfc,
}: Props) {
  const t = useTranslations();
  const ns = "homepage.afc_source_sheet";
  const { height } = useWindowDimensions();
  const existing = useAfcResults();
  const createTestResult = useCreateTestResult();
  const dialogMaxHeight = Math.min(760, Math.max(480, height * 0.85));

  const [value, setValue] = useState<number | null>(null);
  const [date, setDate] = useState<string>(() => getTodayString());
  const [notes, setNotes] = useState("");

  const canSave = value !== null && value >= 0 && !createTestResult.isPending;

  const highValueWarning = value !== null && value > 40;

  const handleClose = () => {
    setValue(null);
    setDate(getTodayString());
    setNotes("");
    onClose();
  };

  const handleSelectExisting = (result: AfcResult) => {
    onSelect({ value: result.value, sourceId: result.id });
    handleClose();
  };

  const handleAddNew = async () => {
    if (value === null || value < 0) return;
    try {
      await createTestResult.mutateAsync({
        name: "Antral Follicle Count",
        type: "imaging",
        imagingTestCategory: "antral_follicle_count",
        patientValue: String(Math.round(value)),
        valueType: "numeric",
        date: date || null,
        notes: notes.trim() || null,
      });
      onSelect({ value: Math.round(value) });
      handleClose();
    } catch {
      // surface via mutation error state
    }
  };

  const handleOpenDocuments = (files: SelectedFile[]) => {
    handleClose();
    onOpenDocuments(files);
  };

  return (
    <Dialog
      containerStyle={{ maxHeight: dialogMaxHeight }}
      visible={open}
      onClose={handleClose}
      closeButton="minimal"
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{
        paddingTop: 24,
        paddingHorizontal: 24,
        paddingBottom: 24,
        maxHeight: dialogMaxHeight,
      }}
    >
      <View style={{ flex: 1, minHeight: 0 }}>
        <DialogIcon
          source={AFC_ILLUSTRATION}
          width={56}
          height={62}
          style={{ marginBottom: 16 }}
        />
        <DialogHeader
          align="center"
          title={t(`${ns}.title`)}
          subtitle={t(`${ns}.subtitle`)}
          style={{ width: "100%", marginBottom: 16 }}
          subtitleStyle={{ textAlign: "center", width: "100%" }}
        />
        <ScrollView
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ gap: 20, paddingBottom: 4 }}
        >
          <TestResultDocumentAction
            onOpenDocuments={handleOpenDocuments}
            primaryLabel={t("care.beta_result.documents_drop_action")}
            secondaryLabel={t("care.beta_result.documents_drop_hint")}
            style={{ marginBottom: 0 }}
          />

          {existing.length > 0 ? (
            <MoonFormField label={t(`${ns}.existing_section`)}>
              <View style={{ gap: 8 }}>
                {existing.map((result) => (
                  <ExistingResultRow
                    key={result.id}
                    result={result}
                    selected={result.id === currentSourceId}
                    onPress={() => handleSelectExisting(result)}
                  />
                ))}
              </View>
            </MoonFormField>
          ) : null}

          <View style={{ gap: 12 }}>
            <MoonNumericField
              label={t(`${ns}.value_label`)}
              labelIcon={Activity}
              labelHint={t(`${ns}.value_hint`)}
              value={value}
              onChange={setValue}
              placeholder="0"
              decimals={0}
              min={0}
              max={100}
            />
            <DateField
              label={t(`${ns}.date_label`)}
              labelIcon={CalendarDays}
              value={date}
              onChange={setDate}
            />
            <TextField
              label={t(`${ns}.notes_label`)}
              labelIcon={PencilLine}
              value={notes}
              onChangeText={setNotes}
              placeholder={t(`${ns}.notes_placeholder`)}
              numberOfLines={3}
              notes
            />
            {highValueWarning ? (
              <FieldMessage tone="warning" style={{ marginTop: 4 }}>
                {t(`${ns}.high_value_warning`)}
              </FieldMessage>
            ) : null}
            {createTestResult.isError ? (
              <FieldMessage style={{ marginTop: 4 }}>
                {t(`${ns}.save_error`)}
              </FieldMessage>
            ) : null}
          </View>

          {hasActiveAfc && onClear ? (
            <CompactActionButton
              label={t(`${ns}.remove_afc`)}
              action={`${ns}.remove_afc`}
              onPress={() => {
                onClear();
                handleClose();
              }}
              variant="secondary"
              size="sm"
              style={{ alignSelf: "flex-start", marginTop: 4 }}
            />
          ) : null}
        </ScrollView>
        <DialogActions style={{ marginTop: 14 }}>
          <SecondaryButton
            label={t("care.beta_result.cancel")}
            onPress={handleClose}
            flex={1}
          />
          <PrimaryButton
            label={
              createTestResult.isPending
                ? t(`${ns}.saving`)
                : t("care.beta_result.save")
            }
            onPress={handleAddNew}
            loading={createTestResult.isPending}
            disabled={!canSave}
            flex={1}
          />
        </DialogActions>
      </View>
    </Dialog>
  );
}
